import { Alert, Button, Loader, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

export type HealthService = 'database' | 'github' | 'queue' | 'kubernetes' | 'db_cloner';

interface ServiceHealthWarningProps {
  services: HealthService[];
  message?: string;
}

interface ServiceHealth {
  service: HealthService;
  status: 'healthy' | 'degraded' | 'down';
  response_time_ms: number | null;
  details: Record<string, any> | null;
  checked_at: string;
  error_message: string | null;
  fetching?: boolean;
}

const SERVICE_LABELS: Record<HealthService, string> = {
  database: 'Database',
  github: 'GitHub API',
  queue: 'Job Queue',
  kubernetes: 'Kubernetes',
  db_cloner: 'Database Cloner'
};

export function ServiceHealthWarning({ services, message }: ServiceHealthWarningProps) {
  const queryClient = useQueryClient();

  const { data: healthData, isLoading, error } = useQuery<ServiceHealth[]>({
    queryKey: ['service-health', services.join(',')],
    queryFn: () => api.getServiceHealth(services),
    refetchInterval: 30000, // Refresh every 30 seconds to match cache TTL
    staleTime: 25000, // Consider data stale after 25 seconds
  });

  const recheckMutation = useMutation({
    mutationFn: () => api.recheckServiceHealth(),
    onSuccess: () => {
      // Invalidate and refetch after 15s to allow container worker health checks to complete
      // Direct checks (database, github, queue) complete in ~1s
      // Container worker checks (kubernetes, db_cloner) take 10-15s
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['service-health'] });
      }, 15000);
    }
  });

  // Show loading state
  if (isLoading) {
    return (
      <Alert
        icon={<Loader size="sm" />}
        title="Checking Service Health"
        color="blue"
        variant="light"
        mb="md"
      >
        <Text size="sm">Verifying system status...</Text>
      </Alert>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Health Check Error"
        color="red"
        variant="light"
        mb="md"
      >
        <Text size="sm">Unable to verify service health. Please try again later.</Text>
      </Alert>
    );
  }

  // Separate fetching services from truly unhealthy ones
  const fetchingServices = healthData?.filter((h) => h.fetching) || [];
  
  // Check if any services are unhealthy (but exclude ones that are just fetching)
  const unhealthyServices = healthData?.filter(
    (h) => (h.status === 'degraded' || h.status === 'down') && !h.fetching
  ) || [];

  // If all services are healthy or just fetching, don't show a warning
  // Fetching is a temporary state and shouldn't be treated as unhealthy
  if (unhealthyServices.length === 0) {
    return null;
  }

  // Determine alert color based on worst status
  const hasDownServices = unhealthyServices.some((s) => s.status === 'down');
  const alertColor = hasDownServices ? 'red' : 'yellow';

  return (
    <Alert
      icon={<IconAlertTriangle size={16} />}
      title="Service Health Warning"
      color={alertColor}
      variant="light"
      mb="md"
    >
      <Stack gap="xs">
        <Text size="sm">
          {message || 'Some services may be experiencing issues'}
        </Text>

        {unhealthyServices.length > 0 && (
          <Text size="sm" c="dimmed">
            <strong>Affected services:</strong>{' '}
            {unhealthyServices
              .map((s) => `${SERVICE_LABELS[s.service]} (${s.status})`)
              .join(', ')}
          </Text>
        )}

        {/* Only show fetching status if there are also unhealthy services */}
        {fetchingServices.length > 0 && unhealthyServices.length > 0 && (
          <Text size="sm" c="dimmed">
            <strong>Also checking:</strong>{' '}
            {fetchingServices.map((s) => SERVICE_LABELS[s.service]).join(', ')}
          </Text>
        )}

        <Button
          size="xs"
          variant="light"
          leftSection={<IconRefresh size={14} />}
          onClick={() => recheckMutation.mutate()}
          loading={recheckMutation.isPending}
          mt="xs"
        >
          Recheck Now
        </Button>
      </Stack>
    </Alert>
  );
}

